import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateDoctorNote,
  EditDoctorNote,
  GetEligibleNoteVisits,
  GetNoteDayUsage,
} from "Services/API/VisitPdf.service";

/**
 * Refresh every list and figure that a note change affects. Both note tables render
 * the same rows under different query keys, and the day budget shifts whenever a note
 * is issued or invalidated.
 */
const invalidateNoteQueries = (queryClient, { patientId, visitId }) => {
  queryClient.invalidateQueries(["patientPdfs", patientId]);
  queryClient.invalidateQueries(["visitPdfs", visitId, patientId]);
  queryClient.invalidateQueries(["noteDayUsage", patientId]);
  queryClient.invalidateQueries(["eligibleNoteVisits", patientId]);
};

/**
 * Days of leave used and remaining in the rolling 30-day window.
 *
 * `excludePdfId` omits one note from the tally. The edit modal passes the note being
 * edited, because that note currently holds its own days — without excluding it a
 * replacement covering the same dates would appear to exceed the budget.
 */
export const useNoteDayUsage = (patientId, excludePdfId) => {
  const query = useQuery({
    queryKey: ["noteDayUsage", patientId, excludePdfId ?? null],
    queryFn: () => GetNoteDayUsage(patientId, excludePdfId),
    enabled: !!patientId,
    staleTime: 0,
  });

  return {
    usage: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

/** Visits the patient paid a doctor note for — the only ones a note may be issued against. */
export const useEligibleNoteVisits = (patientId, enabled = true) => {
  const query = useQuery({
    queryKey: ["eligibleNoteVisits", patientId],
    queryFn: () => GetEligibleNoteVisits(patientId),
    enabled: !!patientId && enabled,
  });

  return {
    visits: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const useCreateDoctorNote = (patientId, visitId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload) => CreateDoctorNote(payload),
    onSuccess: (_data, payload) =>
      invalidateNoteQueries(queryClient, { patientId, visitId: visitId ?? payload?.visitId }),
  });

  return {
    createNote: mutation.mutate,
    createNoteAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error,
    reset: mutation.reset,
  };
};

/**
 * Editing invalidates the existing note and issues a replacement — an issued note is
 * a legal document and is never rewritten in place. The original's days are released
 * first, so re-using the same dates does not collide with its own predecessor.
 */
export const useEditDoctorNote = (patientId, visitId) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ pdfId, ...payload }) => EditDoctorNote(pdfId, payload),
    onSuccess: () => invalidateNoteQueries(queryClient, { patientId, visitId }),
  });

  return {
    editNote: mutation.mutate,
    editNoteAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error,
    reset: mutation.reset,
  };
};
